import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  buildWeddingSlug,
  coupleShortName,
  gmailLabelNameProblem,
  LABEL_PREFIX,
  labelNameForSlug,
  normalizeGmailLabelName,
} from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import WeddingFormFields, { type WeddingFormState } from '@/components/WeddingFormFields';
import CouplePhotoPicker from '@/components/CouplePhotoPicker';

/**
 * Creating a wedding (PRD §F2).
 *
 * The only thing this dialog owns beyond the shared field set is the Gmail
 * label: the name being chosen here becomes a label in the planner's own
 * mailbox and is immutable once the first inquiry goes out, so the planner sees
 * it — and can change it — before they commit to it rather than after.
 *
 * The field starts as a live preview of the derived default and only becomes a
 * choice once the planner types in it. Until then it keeps following the names
 * above it, and the request sends null so the server keeps deriving it too.
 *
 * ## The photo
 *
 * One form, one Save. The picked file waits here rather than uploading itself
 * (which is what `CouplePhotoField` does, correctly, for a wedding that already
 * exists), and rides the create request as multipart when there is one. That is
 * also why the request below is a plain `fetch` in that case: the generated RPC
 * client only serializes JSON, so the no-photo path keeps using it — and keeps
 * its types — while the photo path hand-builds the same payload.
 */

const EMPTY: WeddingFormState = {
  partner1FirstName: '',
  partner1LastName: '',
  partner2FirstName: '',
  partner2LastName: '',
  weddingDate: '',
  startTime: '',
  endTime: '',
  venueName: '',
  venuePlaceId: null,
  venueAddress: null,
  guestCountEstimate: '',
  budgetEstimate: '',
};

export default function NewWeddingDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<WeddingFormState>(EMPTY);
  /** null until the planner edits the label field — see the header comment. */
  const [labelEdit, setLabelEdit] = useState<string | null>(null);
  /** Resized on pick, sent on Create — see the header comment. */
  const [photo, setPhoto] = useState<File | null>(null);

  // Built from the short name, which is what the server derives it from too, so
  // an untouched field previews exactly the label the server will create.
  const slugPreview =
    form.partner1FirstName.trim() && form.partner2FirstName.trim()
      ? buildWeddingSlug(coupleShortName(form), form.weddingDate || null)
      : null;
  const defaultLabel = slugPreview ? labelNameForSlug(slugPreview, LABEL_PREFIX) : '';
  const labelValue = labelEdit ?? defaultLabel;
  const normalizedLabel = normalizeGmailLabelName(labelValue);
  // Only a typed-in name can be wrong; the derived default never is, and an
  // empty form should not show an error before the planner has typed anything.
  const labelProblem = labelEdit === null ? null : gmailLabelNameProblem(normalizedLabel);

  /**
   * What goes on the wire. A name matching the default is sent as null rather
   * than as a pick: the planner who clicked into the field and typed the same
   * thing back meant the default, and null is what keeps it following the
   * couple's names if they edit those before the first send.
   */
  const pickedLabel = normalizedLabel && normalizedLabel !== defaultLabel ? normalizedLabel : null;

  const create = useMutation({
    mutationFn: async () => {
      const payload = {
          partner1FirstName: form.partner1FirstName.trim(),
          partner1LastName: form.partner1LastName.trim() || null,
          partner2FirstName: form.partner2FirstName.trim(),
          partner2LastName: form.partner2LastName.trim() || null,
          // A date input yields `2027-06-12`, which is exactly what the
          // column stores — no packing, no timezone, nothing to undo on read.
          weddingDate: form.weddingDate || null,
          // An untouched or cleared time input reads back as `''`, which the
          // schema's 24-hour regex rejects. On the wire, "not set" is null.
          startTime: form.startTime || null,
          endTime: form.endTime || null,
          venueName: form.venueName.trim() || null,
          venuePlaceId: form.venuePlaceId,
          venueAddress: form.venueAddress,
          guestCountEstimate: form.guestCountEstimate ? Number(form.guestCountEstimate) : null,
          budgetEstimate: form.budgetEstimate ? Number(form.budgetEstimate) : null,
          status: 'lead' as const,
          gmailLabelName: pickedLabel,
      };

      // Same fields either way; only the envelope differs.
      const res = photo
        ? await (() => {
            const body = new FormData();
            body.append('payload', JSON.stringify(payload));
            body.append('photo', photo);
            return fetch('/api/weddings', { method: 'POST', body, credentials: 'include' });
          })()
        : await client.api.weddings.$post({ json: payload });

      if (!res.ok) {
        // The one refusal a planner can act on is a label another wedding
        // already files under, and only the server knows about the others.
        // Its sentence names the label, so it is worth more than ours.
        const message = await res
          .json()
          .then((b: { error?: string }) => b.error)
          .catch(() => null);
        throw new Error(message || 'Failed to create wedding');
      }
      return (await res.json()) as { id: string; photoError?: string };
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['weddings'] });
      onOpenChange(false);
      setForm(EMPTY);
      setLabelEdit(null);
      setPhoto(null);
      // The wedding landed; only its portrait did not. Said plainly, because the
      // form that could have retried it is already gone.
      if (created?.photoError) toast.warning(created.photoError);
      // No "Wedding created" toast on the way: the page that opens is the
      // wedding, which says it better than a message that fades.
      //
      // The draft day, not the board (EVE-56). A wedding one second old has no
      // inquiries and no mail, and the board is a list of gaps; the timeline
      // drafted from the planner's own template is the one reading of this
      // wedding that already has content worth checking. `created=1` is read
      // once, by the notice on that tab, and dropped when it is dismissed —
      // every later visit to a wedding still opens on the board.
      navigate(`/weddings/${created.id}?view=timeline&created=1`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New wedding</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <CouplePhotoPicker value={photo} onChange={setPhoto} couple={form} />

          <WeddingFormFields value={form} onChange={setForm} idPrefix="new" />

          <div className="space-y-2">
            <Label htmlFor="new-gmail-label">Gmail label</Label>
            <Input
              id="new-gmail-label"
              value={labelValue}
              placeholder="VendorOps/emma-liam-2027-06-12"
              aria-invalid={labelProblem ? true : undefined}
              onChange={(e) => setLabelEdit(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {labelProblem ? (
                <span className="text-destructive">{labelProblem}</span>
              ) : (
                <>
                  Where this wedding's mail is filed in your own mailbox. Slashes nest it, so{' '}
                  <code>Clients/Smith</code> lands inside your <code>Clients</code> folder. Fixed
                  once the first inquiry goes out, so your mailbox keeps one stable name for this
                  wedding.
                </>
              )}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !form.partner1FirstName.trim() ||
              !form.partner2FirstName.trim() ||
              labelProblem !== null ||
              create.isPending
            }
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
